<script lang="ts">
	//Stores
	import { user_data } from 'src/stores/account/user';
	//Services
	import { postReq } from '../../../services/http';
	import { format } from '../../../services/i18n';
	//Design system components
	import { ToggleV2, AlertV2, Text, toast } from '@Shelly/shelly-design-system';
	import { timestampToDate } from 'src/helpers/date_helper';
	import RevokeConfirmationModal from './RevokeConfirmationModalV2.svelte';
	import { isSupportAccessExpired } from 'src/helpers/account_helpers';

	let revokeShowModal = false;

	$: isAccessGranted = !!$user_data?.tmpk_valid;
	let toggleChecked = false;

	// Keep the toggle in sync with persisted state unless the revoke modal is open.
	$: if (!revokeShowModal) {
		toggleChecked = isAccessGranted;
	}

	//description and value are reactive because of the timer
	$: description = $user_data?.tmpk_valid
		? $format('info_support_access_granted', {
				values: {
					value: timestampToDate(
						new Date($user_data.tmpk_valid.replace(/\s+/g, 'T')).getTime(),
					),
				},
			})
		: $format('info_grant_account_access_message');

	const grant_access = async () => {
		let res = await postReq('user/get_support_access', {
			'back-door': 'enable',
		});
		const data = res.data;
		if (res.isok) {
			user_data.set({ ...$user_data, tmpk_valid: data.ts });
		} else {
			toggleChecked = isAccessGranted;
			toast.open({ type: 'danger', title: $format('error_global') });
		}
	};

	const disable_access = async () => {
		const supportAccessExpiry = $user_data?.tmpk_valid
			? new Date($user_data.tmpk_valid.replace(/\s+/g, 'T')).getTime()
			: null;

		if (isSupportAccessExpired(supportAccessExpiry)) {
			user_data.remove('tmpk_valid');
			revokeShowModal = false;
			return;
		}

		let res = await postReq('user/remove_support_access', {
			'back-door': 'disable',
		});
		if (res.isok) {
			user_data.remove('tmpk_valid');
		} else {
			toast.open({ type: 'danger', title: $format('error_global') });
		}
		revokeShowModal = false;
	};

	const handleToggle = (checked: boolean) => {
		toggleChecked = checked;
		if (checked) {
			grant_access();
		} else {
			revokeShowModal = true;
		}
	};
</script>

<div class="d-flex gap-s justify-content-between align-items-center">
	<div>
		<Text styleType="body-medium" class="mb-s text-600">
			{$format('collapse_title_grant_account_access', {
				default: 'Grant account access',
			})}
		</Text>
		{#if !$user_data?.tmpk_valid}
			<Text class="mb-s text-color-muted" styleType="body-small">
				{description}
			</Text>
		{/if}
	</div>

	<div class="d-flex align-items-center">
		<ToggleV2
			aria-label={$format('label_grant_account_access', {
				default: 'Grant Account Access',
			})}
			id="toggle-grant-access"
			size="m"
			checked={toggleChecked}
			on:click={() => handleToggle(!toggleChecked)}
		/>
	</div>
</div>

{#if $user_data?.tmpk_valid}
	<AlertV2 backgroundColor="color-00" class="mb-s" heading={description} />
{/if}

<RevokeConfirmationModal
	bind:showModal={revokeShowModal}
	on:close={() => (toggleChecked = isAccessGranted)}
	on:revoke={() => disable_access()}
	mainText={$format('message_modal_revoke_access', {
		default:
			'Revoking account access will immediately disconnect the support and remove their permissions from your account.',
	})}
/>
